"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import RentalCalendar from "../rental-calendar"
import RentalDurationSelector from "../rental-duration-selector"
import RentalPriceDisplay from "../rental-price-display"
import { checkRentalAvailability, getBookedDates } from "@lib/data/rental"
import { addRentalPeriod, getAllowedDateRange } from "@lib/util/rental"
import type { RentalInfo, RentalAvailability } from "@/types/rental"
import type { HttpTypes } from "@medusajs/types"
import { useTranslations, useLocale } from "next-intl"
import { Loader2, CheckCircle, XCircle, Tag, Calendar } from "lucide-react"
import { convertToLocale } from "@lib/util/money"

interface RentalSectionProps {
  product: HttpTypes.StoreProduct
  variant?: HttpTypes.StoreProductVariant
  rentalInfo: RentalInfo
  onAddRental: (params: {
    variantId: string
    rentalPeriod: number
    rentalUnit: string
    startDate: string
    quantity: number
  }) => Promise<void>
  isAdding: boolean
  disabled?: boolean
}

export default function RentalSection({
  product,
  variant,
  rentalInfo,
  onAddRental,
  isAdding,
  disabled = false,
}: RentalSectionProps) {
  const t = useTranslations('product.rental')
  const locale = useLocale()

  // Local state - auto-select tomorrow's date (rental cannot start today)
  const [startDate, setStartDate] = useState<Date | null>(() => {
    // Initialize with tomorrow's date (start of day)
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)
    return tomorrow
  })
  const [duration, setDuration] = useState<number>(rentalInfo.minDuration)
  const [quantity] = useState<number>(1) // For now, fixed at 1

  // Availability state
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [availability, setAvailability] = useState<RentalAvailability | null>(null)
  const [bookedDates, setBookedDates] = useState<Date[]>([])
  const [error, setError] = useState<string | null>(null)

  // Load booked dates on mount
  useEffect(() => {
    const loadBookedDates = async () => {
      const { minDate, maxDate } = getAllowedDateRange()
      // Extend range to cover potential rental period
      const extendedMax = addRentalPeriod(maxDate, rentalInfo.maxDuration || 12, rentalInfo.rentalUnit)
      const dates = await getBookedDates(product.id, minDate, extendedMax)
      setBookedDates(dates)
    }
    loadBookedDates()
  }, [product.id, rentalInfo.maxDuration, rentalInfo.rentalUnit])

  // Check availability when date/duration changes
  const checkAvailability = useCallback(async () => {
    if (!startDate) {
      setAvailability(null)
      return
    }

    setIsCheckingAvailability(true)
    setError(null)

    try {
      const endDate = addRentalPeriod(startDate, duration, rentalInfo.rentalUnit)
      const result = await checkRentalAvailability(product.id, startDate, endDate, quantity)

      setAvailability(result)

      if (result && !result.available) {
        setError(t('unavailable'))
      }
    } catch (err) {
      setError(t('errors.checkFailed'))
    } finally {
      setIsCheckingAvailability(false)
    }
  }, [startDate, duration, rentalInfo.rentalUnit, product.id, quantity, t])

  // Debounced availability check
  useEffect(() => {
    const timeoutId = setTimeout(checkAvailability, 300)
    return () => clearTimeout(timeoutId)
  }, [checkAvailability])

  // Handle add to cart
  const handleAddRental = async () => {
    if (!startDate || !variant) return

    try {
      await onAddRental({
        variantId: variant.id,
        rentalPeriod: duration,
        rentalUnit: rentalInfo.rentalUnit,
        // Send date as UTC midnight to avoid timezone offset issues
        // (e.g., CET 2026-03-24 00:00 → UTC 2026-03-23T23:00 would fail "start from tomorrow" validation)
        startDate: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}T00:00:00.000Z`,
        quantity,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : t('errors.addFailed')
      setError(message)
    }
  }

  // Determine if can add to cart
  const canAddToCart =
    startDate !== null &&
    variant !== undefined &&
    duration >= rentalInfo.minDuration &&
    (rentalInfo.maxDuration === null || duration <= rentalInfo.maxDuration) &&
    availability?.available === true &&
    !isAdding &&
    !disabled

  // Calculate hover preview price string for the calendar
  const hoverPreview = useMemo(() => {
    if (!rentalInfo.rentalPrice) return null
    const totalForDuration = rentalInfo.rentalPrice * duration
    const localeMap: Record<string, string> = {
      ru: 'ru-RU', en: 'en-US', cs: 'cs-CZ', uk: 'uk-UA',
    }
    return convertToLocale({
      amount: totalForDuration,
      currency_code: rentalInfo.currencyCode,
    })
  }, [rentalInfo.rentalPrice, duration, rentalInfo.currencyCode, locale])

  // Calculate next weekend (Fri-Mon) for quick preset
  const getNextWeekend = useCallback((): { friday: Date; duration: number } | null => {
    if (rentalInfo.rentalUnit !== 'day') return null

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const dayOfWeek = today.getDay() // 0=Sun, 5=Fri, 6=Sat

    // Find the next Friday that is >= tomorrow
    let daysUntilFriday: number
    if (dayOfWeek === 5) {
      // Today is Friday, use next Friday
      daysUntilFriday = 7
    } else if (dayOfWeek === 6) {
      // Today is Saturday, use next Friday
      daysUntilFriday = 6
    } else if (dayOfWeek === 0) {
      // Today is Sunday, use next Friday
      daysUntilFriday = 5
    } else {
      // Mon-Thu: this coming Friday
      daysUntilFriday = 5 - dayOfWeek
      if (daysUntilFriday <= 0) daysUntilFriday += 7
    }

    // Ensure the Friday is at least tomorrow
    if (daysUntilFriday < 1) daysUntilFriday += 7

    const friday = new Date(today)
    friday.setDate(today.getDate() + daysUntilFriday)
    friday.setHours(0, 0, 0, 0)

    // Check min/max duration - weekend is 3 days
    if (rentalInfo.minDuration > 3) return null
    if (rentalInfo.maxDuration !== null && rentalInfo.maxDuration < 3) return null

    // Check if the Friday is within the allowed range
    const { maxDate: allowedMax } = getAllowedDateRange()
    if (friday > allowedMax) return null

    return { friday, duration: 3 }
  }, [rentalInfo.rentalUnit, rentalInfo.minDuration, rentalInfo.maxDuration])

  const weekendPreset = getNextWeekend()

  const handleWeekendPreset = () => {
    if (!weekendPreset) return
    setStartDate(weekendPreset.friday)
    setDuration(weekendPreset.duration)
  }

  // Build auto-discount banner text
  const autoDiscountBanner = (() => {
    const preview = rentalInfo.autoDiscountPreview
    if (!preview) return null

    const config = preview.trigger_config
    if (preview.trigger_type === 'duration' && config?.min_duration) {
      const unit = config.min_duration_unit || 'day'
      if (preview.discount_type === 'percentage') {
        return t('autoDiscountBannerPercent', {
          duration: config.min_duration,
          unit,
          value: preview.discount_value,
        })
      }
      return t('autoDiscountBannerFixed', {
        duration: config.min_duration,
        unit,
        value: preview.discount_value,
      })
    }
    return null
  })()

  return (
    <div className="space-y-4 p-4 bg-surface rounded-lg border border-border">
      {/* Auto-discount banner */}
      {autoDiscountBanner && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-md text-sm text-success">
          <Tag className="h-4 w-4 shrink-0" />
          <span>{autoDiscountBanner}</span>
        </div>
      )}

      {/* Weekend preset button */}
      {weekendPreset && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleWeekendPreset}
          disabled={disabled || isAdding}
          className="w-full flex items-center justify-center gap-2"
          data-testid="rental-weekend-preset"
        >
          <Calendar className="h-4 w-4" />
          {t('weekendPreset')}
        </Button>
      )}

      {/* Calendar */}
      <RentalCalendar
        selectedDate={startDate}
        onDateSelect={setStartDate}
        bookedDates={bookedDates}
        disabled={disabled || isAdding}
        hoverPreview={hoverPreview}
      />

      {/* Duration selector */}
      <RentalDurationSelector
        rentalUnit={rentalInfo.rentalUnit}
        rentalPrice={rentalInfo.rentalPrice || 0}
        minDuration={rentalInfo.minDuration}
        maxDuration={rentalInfo.maxDuration}
        selectedDuration={duration}
        currencyCode={rentalInfo.currencyCode}
        onDurationChange={setDuration}
        disabled={disabled || isAdding}
      />

      {/* Availability status */}
      {startDate && (
        <div className="flex items-center gap-2 text-sm">
          {isCheckingAvailability ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-foreground-secondary" />
              <span className="text-foreground-secondary">{t('checkingAvailability')}</span>
            </>
          ) : availability ? (
            availability.available ? (
              <>
                <CheckCircle className="h-4 w-4 text-success" />
                <span className="text-success">{t('available')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="text-destructive">{t('unavailable')}</span>
              </>
            )
          ) : null}
        </div>
      )}

      {/* Price display */}
      {startDate && (
        <RentalPriceDisplay
          rentalPrice={rentalInfo.rentalPrice || 0}
          depositAmount={rentalInfo.depositAmount || 0}
          duration={duration}
          quantity={quantity}
          rentalUnit={rentalInfo.rentalUnit}
          currencyCode={rentalInfo.currencyCode}
          startDate={startDate}
        />
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {/* Add to cart button */}
      <Button
        type="button"
        variant="primary"
        size="lg"
        onClick={handleAddRental}
        disabled={!canAddToCart}
        isLoading={isAdding}
        className="w-full"
        data-testid="add-rental-button"
      >
        {t('addRental')}
      </Button>
    </div>
  )
}
